import { Settings } from "./Settings";
import { SceneManager } from "../scene/SceneManager";
import { PlaybackController } from "./PlaybackController";
import { HardcodedTourSource } from "../data/HardcodedTourSource";
import { ApiTourSource } from "../data/ApiTourSource";
import type { TourStrategy } from "../data/ApiTourSource";
import { CoinApi } from "../api/CoinApi";
import type { TourSource } from "../playback/TourSource";
import { Overlay } from "../ui/Overlay";
import { Controls } from "../ui/Controls";
import { TourSonifier } from "../audio/TourSonifier";

/** Upper bound on a single frame's simulated delta time, in seconds. */
const MAX_FRAME_DT_SECONDS = 1 / 20;

/**
 * Phase G: the live `GET /solutions` API (via `ApiTourSource`) is now the
 * default tour source, matching production (proxy mode, same-origin
 * `coin-api.php`). `VITE_TOUR_SOURCE=hardcoded` is a dev-only escape hatch
 * back to the Phase F fixtures — see `.env.example`. Nothing else in the
 * app (state machine, camera, animation) knows or cares which one is
 * active; both satisfy the same `TourSource` interface.
 */
function createTourSource(): TourSource {
  if (import.meta.env.VITE_TOUR_SOURCE === "hardcoded") {
    return new HardcodedTourSource();
  }
  const api = new CoinApi({
    mode: import.meta.env.VITE_COIN_API_MODE,
    baseUrl: import.meta.env.VITE_COIN_API_BASE_URL,
  });
  const strategy = import.meta.env.VITE_TOUR_STRATEGY as TourStrategy | undefined;
  return new ApiTourSource(api, { strategy });
}

export class CoinArtApp {
  private settings = new Settings();
  private scene: SceneManager;
  private playback: PlaybackController;
  private overlay: Overlay;
  private controls: Controls;
  private sonifier = new TourSonifier(this.settings);
  private rafHandle = 0;
  private lastTimestamp = 0;
  private tabVisible = true;
  private readonly onVisibilityChange = (): void => this.handleVisibilityChange();

  constructor(root: HTMLElement) {
    if (new URLSearchParams(window.location.search).get("scene") === "aquarium") {
      this.settings.setSceneMode("aquarium");
    }
    this.scene = new SceneManager(root, this.settings);
    this.playback = new PlaybackController(this.scene, createTourSource(), this.settings, this.sonifier);
    this.overlay = new Overlay(root, this.settings);
    this.controls = new Controls(root, this.settings, () => this.playback.skipToNextTour(), this.sonifier);

    document.addEventListener("visibilitychange", this.onVisibilityChange);
  }

  start(): void {
    this.lastTimestamp = performance.now();
    this.rafHandle = requestAnimationFrame(this.loop);
  }

  private loop = (timestamp: number): void => {
    this.rafHandle = requestAnimationFrame(this.loop);
    const rawDtSeconds = (timestamp - this.lastTimestamp) / 1000;
    this.lastTimestamp = timestamp;

    // While the tab is hidden, rAF is throttled or stopped by the browser
    // anyway; skip simulation explicitly too so a delayed callback right
    // after becoming hidden can't apply a large delta.
    if (!this.tabVisible) return;

    const dt = Math.min(Math.max(rawDtSeconds, 0), MAX_FRAME_DT_SECONDS);
    this.playback.update(dt);
    this.scene.render();
    this.overlay.update(this.playback.getSnapshot());
  };

  private handleVisibilityChange(): void {
    this.tabVisible = document.visibilityState === "visible";
    this.sonifier.suspendForHidden(!this.tabVisible);
    if (this.tabVisible) {
      // Resync the clock so the next frame's delta is small rather than the
      // entire hidden duration — prevents the state machine from skipping
      // dozens of moves to "catch up" after the tab regains focus.
      this.lastTimestamp = performance.now();
    }
  }

  dispose(): void {
    cancelAnimationFrame(this.rafHandle);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.controls.dispose();
    this.overlay.dispose();
    this.playback.dispose();
    this.scene.dispose();
  }
}
